import { Owner } from "src/business/account/owner";
import { useOrganization } from "src/data/admin/queries/organization";
import { RestaurantSummary } from "src/data/admin/types/organization";
import { isPaymentSurfaceEnabled } from "src/helpers/FeatureCapabilityConfig";

// Stable identity, so consumers can key effects on the result.
const NO_RESTAURANTS: RestaurantSummary[] = [];

/**
 * The restaurants the caller's organization holds, read from the organization
 * detail endpoint (`GET /web/organizations/{id}`).
 *
 * `GET /web/restaurants` only carries restaurants with an active subscription, so
 * an owner who unsubscribed gets an empty dashboard list while the organization
 * keeps the restaurant. This is how the UI finds it again — enough to reach its
 * payment history and pay for reinstatement.
 *
 * Requested only when `enabled` and the caller owns the organization: payment
 * access is organization-owner-scoped, so there is nothing to offer anyone else.
 */
export const useOrganizationRestaurants = (
  user: Owner | null,
  enabled: boolean,
): { restaurants: RestaurantSummary[]; isLoading: boolean } => {
  const { data, isLoading } = useOrganization(
    user?.organizationId ?? "",
    enabled && isPaymentSurfaceEnabled(user),
  );

  return {
    restaurants: data?.restaurants ?? NO_RESTAURANTS,
    // `isLoading`, not `isPending`: a query held back by `enabled` stays pending
    // forever, and that is not something to show a spinner for.
    isLoading,
  };
};
